import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

// ─── Animated counter that counts up from 0 to target ───
const AnimCounter = ({ target, prefix = '', suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const observed = useRef(false);
  const elRef = useRef(null);

  useEffect(() => {
    if (!target || target <= 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !observed.current) {
          observed.current = true;
          const start = performance.now();
          const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) ref.current = requestAnimationFrame(step);
          };
          ref.current = requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );

    if (elRef.current) observer.observe(elRef.current);
    return () => {
      observer.disconnect();
      if (ref.current) cancelAnimationFrame(ref.current);
    };
  }, [target, duration]);

  return <span ref={elRef}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

/**
 * PlatformStats — replaces the old newsletter section with a real-data
 * transparency dashboard that builds credibility and engagement.
 */
const PlatformStats = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          { count: peopleCount },
          { count: companyCount },
          { count: pacCount },
          { count: execCount },
          { count: investCount },
          { count: pacAssocCount },
          { data: revenueData },
        ] = await Promise.all([
          supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
          supabase.from('companies').select('*', { count: 'exact', head: true }),
          supabase.from('political_action_committees').select('*', { count: 'exact', head: true }),
          supabase.from('executives').select('*', { count: 'exact', head: true }),
          supabase.from('investments').select('*', { count: 'exact', head: true }),
          supabase.from('pac_relationships').select('*', { count: 'exact', head: true }),
          supabase.from('companies').select('revenue'),
        ]);

        const totalRevenue = (revenueData || []).reduce((sum, c) => sum + (c.revenue || 0), 0);
        const connections = (execCount || 0) + (investCount || 0) + (pacAssocCount || 0);

        setStats({
          people: peopleCount || 0,
          companies: companyCount || 0,
          pacs: pacCount || 0,
          connections,
          totalRevenue,
          industries: new Set((revenueData || []).map(c => c.industry)).size || 12,
        });
      } catch (err) {
        console.error('Platform stats error:', err);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return null;

  const formatRevenue = (amount) => {
    if (amount >= 1e12) return `$${(amount / 1e12).toFixed(1)}T`;
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(0)}B`;
    return `$${(amount / 1e6).toFixed(0)}M`;
  };

  const tiles = [
    { label: 'Individuals Tracked', value: stats.people, icon: 'Users', color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { label: 'Companies Monitored', value: stats.companies, icon: 'Building2', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    { label: 'Corporate Revenue Tracked', value: stats.totalRevenue, icon: 'DollarSign', color: 'text-amber-600 bg-amber-50 border-amber-200', format: formatRevenue },
    { label: 'PACs Catalogued', value: stats.pacs, icon: 'Landmark', color: 'text-purple-600 bg-purple-50 border-purple-200' },
    { label: 'Connections Mapped', value: stats.connections, icon: 'Link2', color: 'text-rose-600 bg-rose-50 border-rose-200' },
    { label: 'Data Sources', value: 6, icon: 'Database', color: 'text-cyan-600 bg-cyan-50 border-cyan-200', static: true },
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white relative overflow-hidden">
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-sm font-medium text-emerald-400 border border-white/10 mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            Live Platform Data
          </div>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">
            Transparency at Scale
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Real numbers. Real data. All publicly sourced from Wikipedia, SEC filings, and FEC records.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {tiles.map((tile, i) => (
            <div
              key={i}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:bg-white/10 transition-all duration-300 text-center group"
            >
              <div className={`w-10 h-10 mx-auto mb-3 rounded-lg flex items-center justify-center border ${tile.color}`}>
                <Icon name={tile.icon} size={18} />
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                {tile.static ? (
                  tile.value
                ) : tile.format ? (
                  tile.format(tile.value)
                ) : (
                  <AnimCounter target={tile.value} suffix="+" />
                )}
              </div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                {tile.label}
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <Icon name="Shield" size={14} className="text-emerald-500" />
            <span>Publicly Sourced Data</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="RefreshCw" size={14} className="text-blue-400" />
            <span>Updated Regularly</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="Lock" size={14} className="text-amber-400" />
            <span>No Account Required</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="Globe" size={14} className="text-purple-400" />
            <span>100% Free</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformStats;
